import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/attendance/shifts")({
  component: RouteComponent,
});

function RouteComponent() {
  return <ShiftDataTable />;
}

import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import {
  ShiftFormSheet,
  type ShiftFormData,
} from "@/components/crud/forms/ShiftFormSheet";

// Hooks
import useFetchShifts, {
  type ShiftOrdering,
} from "@/hooks/api/shift/useFetchShift";
import useCreateShift from "@/hooks/api/shift/useCreateShift";
import useUpdateShift from "@/hooks/api/shift/useUpdateShift";
import useDeleteShift from "@/hooks/api/shift/useDeleteShift";
import { ShiftTable } from "@/components/crud/table/ShiftTable";
import type { Shift } from "@/types/shift";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { useDebounce } from "@/hooks/use-debounce";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function ShiftDataTable() {
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "attendance.shift.manage");
  // UI State
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  const [shiftToDelete, setShiftToDelete] = useState<Shift | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        name: SortDirection;
        start_time: SortDirection;
        created_at: SortDirection;
      },
      ShiftOrdering
    >({
      name: null,
      start_time: null,
      created_at: null,
    });

  const [overnightFilter, setOvernightFilter] = useState<
    "true" | "false" | "clear" | undefined
  >();

  // Global Search Filter (No search user needed since shifts type have no user field)
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(globalFilter, 500);

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  // Shift Crud Hooks
  const shiftsQuery = useFetchShifts({
    page_size: pageSize,
    page,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
    is_overnight:
      overnightFilter === "true"
        ? true
        : overnightFilter === "false"
          ? false
          : undefined,
  });
  const shifts = shiftsQuery.data?.results ?? [];
  const count = shiftsQuery.data?.count ?? 0;

  const createShift = useCreateShift();
  const updateShift = useUpdateShift();
  const deleteShift = useDeleteShift();

  // Combined Loading State
  const isMutating =
    createShift.isPending || updateShift.isPending || deleteShift.isPending;
  const isLoading = shiftsQuery.isPending || isMutating;
  const isFetching = shiftsQuery.isFetching || isMutating;

  // For resetting after create, update and delete
  const resetFilters = () => {
    setOrdering({
      name: null,
      start_time: null,
      created_at: null,
    });
    setOvernightFilter(undefined);
    setPage(1);
    setGlobalFilter("");
    resetDebounce();
  };

  // Handlers
  const handleCreate = () => {
    setEditingShift(null);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleEdit = (shift: Shift) => {
    setEditingShift(shift);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleDeleteClick = (shift: Shift) => {
    setShiftToDelete(shift);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!shiftToDelete) return;
    try {
      await deleteShift.mutateAsync({ id: shiftToDelete.id });
      toast.success("Shift deleted successfully!");
      setIsDeleteDialogOpen(false);
      setShiftToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleFormSubmit = async (data: ShiftFormData) => {
    if (editingShift) {
      await updateShift.mutateAsync({ id: editingShift.id, payload: data });
      toast.success("Shift updated successfully!");
    } else {
      await createShift.mutateAsync(data);
      toast.success("Shift created successfully!");
    }

    setIsSheetOpen(false);
  };

  const totalPages = useMemo(() => {
    return count ? Math.ceil(count / pageSize) : 1;
  }, [count, pageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, overnightFilter, setPage]);

  const [showFilters, setShowFilters] = useState(false);

  const [isViewOnly, setIsViewOnly] = useState(false); // 1. Add state

  const handleView = (shift: Shift) => {
    setEditingShift(shift);
    setIsViewOnly(true); // 4. Set view mode
    setIsSheetOpen(true);
  };

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Shifts"
          description="Manage work shifts here."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={canManage ? handleCreate : undefined}
          addLabel="Create Shift"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search shifts..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-8 max-w-sm font-light"
            />
          </div>
          {/* Shift Type Filter */}
          <SelectFilter
            placeholder="Shift Type"
            options={[
              { label: "Regular", value: "false" },
              { label: "Overnight", value: "true" },
              { label: "Clear", value: "clear" },
            ]}
            value={overnightFilter}
            onChange={(value) => {
              if (value === "clear") {
                setOvernightFilter(undefined); // reset to default
              } else {
                setOvernightFilter(value);
              }
            }}
          />
        </FiltersPanel>

        {/* Shifts Table */}
        <ShiftTable
          shifts={shifts}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          canManage={canManage}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        {/* Shift Form Sheet */}
        <ShiftFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleFormSubmit}
          editingShift={editingShift}
          isViewOnly={isViewOnly}
          isLoading={isMutating}
        />

        {/* Delete Confirmation */}
        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Shift"
          description={`Are you sure you want to delete shift "${shiftToDelete?.name}"? This action cannot be undone.`}
          itemName={shiftToDelete?.name}
          isLoading={isMutating}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
